/**
 * SidebarStep7 - Final Review & Submit (Historical BI Path)
 * 
 * Purpose: Give the user a calm, high-level overview and a single "Submit Claim" action.
 * This prevents accidental submissions and feels professional.
 * 
 * Features:
 *   - Final Claim Amount (prominent display)
 *   - Key Summary (business, event, mode, sources)
 *   - Calculation Highlights (BI breakdown)
 *   - Download Links (all report types)
 *   - Single Submit Button → Moves claim to dashboard
 *   - Read-only review - no new inputs
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/TestAuthContext';
import axios from '@/lib/http';
import api from '../../../utils/api';
import WizardV2Layout from './WizardV2Layout';
import type { QAItem } from './WizardSidebar';
import { FinancialValue } from '../../shared/FinancialValue';
import { formatCurrency } from '../../../utils/financialFormat';
import { useActivityLog } from '../../../hooks/useActivityLog';
import { getTriggerStatus, type TriggerStatus } from '../../../services/deliberationApi';
import { 
  CheckCircle, 
  FileText, 
  Download,
  Building,
  Calendar,
  Calculator,
  BarChart3,
  Loader2,
  AlertTriangle,
  Sparkles,
  Package,
  ArrowRight,
  Shield,
  TrendingUp
} from 'lucide-react';

interface SidebarStep7Props {
  goToStep?: (step: number) => void;
  onBack: () => void;
  onComplete: () => void;
  data: any;
  onSave?: (explicitData?: any) => Promise<void>;
  isSaving?: boolean;
  lastSaved?: Date | null;
  highestCompletedStep?: number;
  backendCompletionPercent?: number;
  claimId?: string;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Read-only summary of the downstream War Room deliberation for this claim.
 * The War Room runs AFTER the Data Specialist + Accountant review (post-submit),
 * so for a claim mid-wizard it shows an honest "runs after review" state; once
 * the deliberation has run it surfaces the forensic defensible range +
 * recommended recovery (the SAME figures the /deliberation page shows) and a
 * link to the full deliberation. Never computes or fabricates figures — it only
 * renders what the War Room forensic agent landed on (or an honest empty state).
 */
function WarRoomConsensusCard({ reportId }: { reportId?: string }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<TriggerStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(!!reportId);

  useEffect(() => {
    if (!reportId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const s = await getTriggerStatus(reportId);
        if (!cancelled) setStatus(s);
      } catch {
        if (!cancelled) setStatus(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reportId]);

  const noRun = !status || status.status === 'no_analysis' || status.status === 'unknown';
  const rec = status?.recommended_recovery ?? null;
  const low = status?.forensic_range?.low ?? null;
  const high = status?.forensic_range?.high ?? null;
  const hasForensic =
    typeof rec === 'number' || typeof low === 'number' || typeof high === 'number';

  return (
    <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
      <div className="flex items-center gap-2 mb-4">
        <Shield className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
        <h3 className="cs-h3">War Room Consensus</h3>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 cs-meta" style={{ color: 'var(--color-text-secondary)' }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Checking deliberation status…
        </div>
      ) : noRun ? (
        <p className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>
          The War Room forensic / adjuster / attorney deliberation runs after the Data
          Specialist and Accountant review this claim. Its defensible range and recommended
          recovery will appear here once complete.
        </p>
      ) : !hasForensic ? (
        <div className="space-y-3">
          <p className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>
            War Room status: <strong>{status?.status}</strong>. Forensic figures not available yet.
          </p>
          {reportId && (
            <button
              onClick={() => navigate(`/deliberation/${reportId}`)}
              className="cs-body inline-flex items-center gap-1 font-medium"
              style={{ color: 'var(--color-brand)' }}
            >
              View deliberation <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {typeof rec === 'number' && (
              <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)' }}>
                <p className="cs-label">Recommended Recovery</p>
                <p className="cs-figure" style={{ fontSize: '1.375rem', color: 'var(--color-text-primary)' }}>
                  <FinancialValue value={rec} cents />
                </p>
              </div>
            )}
            {(typeof low === 'number' || typeof high === 'number') && (
              <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)' }}>
                <p className="cs-label">Defensible Range</p>
                <p className="cs-figure" style={{ fontSize: '1.375rem', color: 'var(--color-text-primary)' }}>
                  {typeof low === 'number' ? <FinancialValue value={low} cents /> : '—'} – {typeof high === 'number' ? <FinancialValue value={high} cents /> : '—'}
                </p>
              </div>
            )}
          </div>
          {typeof status?.attorney_target === 'number' && (
            <p className="cs-meta" style={{ color: 'var(--color-text-secondary)' }}>
              Attorney settlement target: <FinancialValue value={status.attorney_target} cents />
            </p>
          )}
          {reportId && (
            <button
              onClick={() => navigate(`/deliberation/${reportId}`)}
              className="cs-body inline-flex items-center gap-1 font-medium"
              style={{ color: 'var(--color-brand)' }}
            >
              View full War Room deliberation <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function SidebarStep7({
  onBack,
  onComplete,
  goToStep,
  data,
  onSave,
  isSaving,
  lastSaved,
  highestCompletedStep,
  backendCompletionPercent,
  claimId
}: SidebarStep7Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  
  const { getToken } = useAuth();
  const { logActivity } = useActivityLog();
  const navigate = useNavigate();

  // Hydration fallback: when a claim is RESUMED, its wizard `data` subdocument
  // may lack `calculation_results` / `client_name` (they were persisted to the
  // generated report in db.reports, not written back into draft.data). Without
  // this, Final Review shows $0.00 and "Unknown Business" even though the report
  // (and the War Room) have the real figures. Pull the persisted result +
  // business name from the draft endpoint and use them ONLY as a fallback when
  // the live wizard data is empty. Read-only — never overrides real wizard data.
  const [saved, setSaved] = useState<
    { result?: any; clientName?: string; finalClaim?: number } | null
  >(null);

  useEffect(() => {
    if (!claimId) return;
    let cancelled = false;
    (async () => {
      try {
        const d: any = await api.get(`/api/v1/reports/drafts/${claimId}`, { showErrorToast: false });
        if (!cancelled && d) {
          setSaved({
            result: d.saved_result ?? null,
            clientName: d.saved_client_name ?? undefined,
            finalClaim: typeof d.saved_final_claim_amount === 'number' ? d.saved_final_claim_amount : undefined,
          });
        }
      } catch {
        /* best-effort — no persisted result to hydrate from */
      }
    })();
    return () => { cancelled = true; };
  }, [claimId]);

  // Handle authenticated PDF download
  const handleDownloadPdf = async () => {
    if (!reportId) return;
    
    try {
      // Use centralized API utility for blob download.
      // (Removed `responseType: 'blob'` — axios-era option not in
      // ApiOptions. apiFetch auto-returns a Blob when server
      // content-type is pdf/octet-stream/vnd. 2026-04-26 archaeology.)
      const blob = await api.get(`/api/v1/download/report-pdf/${reportId}`, {
        showErrorToast: true,
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `BI_Report_${reportId.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      logActivity({
        reportId,
        actionType: 'download',
        actionDetails: 'Downloaded Standard Report PDF',
        section: 'step7',
        metadata: { download_type: 'standard_pdf' }
      });
    } catch (error) {
      console.error('PDF download failed:', error);
      setDownloadError(`Download failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Extract data from wizard state
  const reportId = data.report_id || data.generated_report_id;
  // Prefer live wizard data; fall back to the persisted report result (saved)
  // for a resumed claim whose draft.data was never populated with calc results.
  const liveCalc = data.calculation_results && Object.keys(data.calculation_results).length
    ? data.calculation_results
    : null;
  const calculationResults = liveCalc || saved?.result || {};
  const finalClaimAmount =
    calculationResults.final_claim_amount || data.final_claim_amount || saved?.finalClaim || 0;
  
  // Key details
  const clientName = data.client_name || saved?.clientName || 'Unknown Client';
  const claimantName = data.claimant_name || data.business_name || 'Unknown Business';
  const eventType = data.event_type || 'Business Interruption';
  const eventStartDate = data.event_start_date;
  const eventEndDate = data.event_end_date;
  
  // Calculation highlights
  const calculationDetail = calculationResults.calculation_detail || {};
  const vpLoss = calculationDetail.final_calculation?.step1_vp_loss;
  const incrementalLoss = calculationDetail.final_calculation?.step2_incremental;
  const additionalExpenses = calculationDetail.final_calculation?.additional_expenses;
  
  // Monte Carlo results (if available)
  const monteCarloResults = data.monte_carlo_results || calculationResults.monte_carlo;
  const hasMonteCarloData = !!monteCarloResults?.results;

  // Policy Coverage - for limit validation
  const policyCoverage = data.policy_coverage || data.policy_data || {};
  const claimType = policyCoverage.claim_type || data.claim_type || 'unknown';
  const policyLimit = policyCoverage.combined_limit || policyCoverage.bi_limit || policyCoverage.limits?.aggregate_limit || 0;
  
  // Policy limit validation
  const getPolicyLimitStatus = () => {
    // Skip validation for third-party claims or unknown claim types
    if (claimType === 'third_party' || claimType === 'unknown' || !policyLimit) {
      return null;
    }
    
    const percentage = (finalClaimAmount / policyLimit) * 100;
    const isOverLimit = finalClaimAmount > policyLimit;
    const isNearLimit = percentage >= 80 && !isOverLimit;
    
    if (isOverLimit) {
      return {
        type: 'error' as const,
        title: 'Exceeds Policy Limit',
        message: `Your calculated claim (${formatCurrency(finalClaimAmount, { cents: true })}) exceeds the policy coverage limit (${formatCurrency(policyLimit)}) by ${formatCurrency(finalClaimAmount - policyLimit, { cents: true })}.`,
        percentage,
        excess: finalClaimAmount - policyLimit
      };
    }
    
    if (isNearLimit) {
      return {
        type: 'warning' as const,
        title: 'Near Policy Limit',
        message: `Your claim is using ${percentage.toFixed(1)}% of the available policy coverage (${formatCurrency(policyLimit)}).`,
        percentage,
        excess: 0
      };
    }
    
    return {
      type: 'success' as const,
      title: 'Within Coverage',
      message: `Your claim is within policy limits (${percentage.toFixed(1)}% of ${formatCurrency(policyLimit)}).`,
      percentage,
      excess: 0
    };
  };
  
  const policyLimitStatus = getPolicyLimitStatus();

  // Submit the claim — updates draft status for accountant handoff
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const token = await getToken();

      // Update draft status to pending accountant review
      // The draft stays intact — accountant picks it up for P&L reconstruction
      const targetId = claimId || reportId;
      if (targetId) {
        await axios.put(
          `${BACKEND_URL}/api/v1/reports/${targetId}/update`,
          {
            status: 'pending_accountant_review',
            submitted_at: new Date().toISOString(),
            final_claim_amount: finalClaimAmount,
            calculation_mode: 'standard_bi',
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }

      // Log the submission
      logActivity({
        reportId: reportId || 'unknown',
        actionType: 'submit',
        actionDetails: `Submitted BI claim for ${claimantName}`,
        section: 'wizard',
        metadata: {
          calculation_mode: 'standard_bi',
          final_claim_amount: finalClaimAmount
        }
      });

      // NOTE: Do NOT clear wizard_backup — draft persists for accountant review

      setIsSubmitted(true);

    } catch (err: any) {
      console.error('Submission failed:', err);
      setSubmitError(err.response?.data?.detail || 'Failed to submit claim. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Build sidebar sections
  const buildSections = () => [
    {
      id: 'summary',
      label: 'Claim Summary',
      isActive: true,
      isComplete: true,
      onClick: () => document.getElementById('summary-section')?.scrollIntoView({ behavior: 'smooth' })
    },
    {
      id: 'details',
      label: 'Key Details',
      isActive: false,
      isComplete: true,
      onClick: () => document.getElementById('details-section')?.scrollIntoView({ behavior: 'smooth' })
    },
    {
      id: 'calculation',
      label: 'Calculation Highlights',
      isActive: false,
      isComplete: true,
      onClick: () => document.getElementById('calculation-section')?.scrollIntoView({ behavior: 'smooth' })
    },
    {
      id: 'downloads',
      label: 'Available Downloads',
      isActive: false,
      isComplete: true,
      onClick: () => document.getElementById('downloads-section')?.scrollIntoView({ behavior: 'smooth' })
    }
  ];

  // Build QA items
  // QAItem.status canonical literals: 'missing' | 'warning' | 'complete'.
  // Aligned with WizardSidebar.tsx per pattern §5 (PR #233).
  const buildQAItems = (): QAItem[] => {
    const items: QAItem[] = [];

    items.push({ id: 'steps', label: 'All steps complete', status: 'complete' });
    items.push({ id: 'report', label: 'Report generated', status: reportId ? 'complete' : 'warning' });
    items.push({ id: 'claim', label: `Claim: ${formatCurrency(finalClaimAmount, { cents: true })}`, status: finalClaimAmount > 0 ? 'complete' : 'warning' });
    
    // Add policy limit check to QA items
    if (policyLimitStatus) {
      if (policyLimitStatus.type === 'error') {
        items.push({ id: 'policy', label: 'Exceeds policy limit', status: 'warning' });
      } else if (policyLimitStatus.type === 'warning') {
        items.push({ id: 'policy', label: 'Near policy limit', status: 'warning' });
      } else {
        items.push({ id: 'policy', label: 'Within policy limits', status: 'complete' });
      }
    }
    
    return items;
  };

  // Success screen after submission
  if (isSubmitted) {
    return (
      <WizardV2Layout
        currentStep={7}
        highestCompletedStep={highestCompletedStep}
        stepTitle="Claim Submitted"
        stepSubtitle="Your claim has been saved to the dashboard"
        completionPercent={backendCompletionPercent ?? 100}
        calculationMode={data.calculation_mode || 'standard_bi'}
        grade="A"
        sections={buildSections()}
        qaItems={[{ id: 'submitted', label: 'Claim submitted successfully', status: 'complete' }]}
        isSaving={false}
        lastSaved={lastSaved}
        onSave={onSave}
        goToStep={goToStep}
        onBack={onBack}
        showNext={false}
        claimId={claimId}
      >
        <div className="p-6 lg:p-8">
          <div className="max-w-2xl mx-auto text-center py-12">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'transparent' }}>
              <CheckCircle className="w-10 h-10" style={{ color: 'var(--color-verified)' }} />
            </div>
            <h2 className="cs-display mb-4">Claim Submitted Successfully!</h2>
            <p className="cs-body mb-8">
              Your BI claim for <strong>{claimantName}</strong> has been submitted and saved to your dashboard.
            </p>

            <div className="rounded-xl p-6 mb-8" style={{ background: 'var(--color-brand)' }}>
              <p className="cs-label mb-2" style={{ color: 'var(--color-on-brand)' }}>Final Claim Amount</p>
              <p className="cs-figure-lg" style={{ color: 'var(--color-on-brand)' }}><FinancialValue value={finalClaimAmount} cents /></p>
              {reportId && <p className="cs-meta mt-2" style={{ color: 'var(--color-on-brand)' }}>Report ID: {reportId}</p>}
            </div>

            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/')}
                className="cs-body px-8 py-3 rounded-xl font-semibold transition-colors flex items-center gap-2"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
              >
                View Dashboard
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={onComplete}
                className="cs-body px-8 py-3 rounded-xl font-semibold transition-colors"
                style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-bg-elevated)'; }}
              >
                Create Another Claim
              </button>
            </div>
          </div>
        </div>
      </WizardV2Layout>
    );
  }

  return (
    <WizardV2Layout
      currentStep={7}
      highestCompletedStep={highestCompletedStep}
      stepTitle="Final Review & Submit"
      stepSubtitle="Review your claim before submission"
      completionPercent={backendCompletionPercent ?? 100}
      calculationMode={data.calculation_mode || 'standard_bi'}
      grade="A"
      sections={buildSections()}
      qaItems={buildQAItems()}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      showNext={false}
      claimId={claimId}
    >
      <div className="p-6 lg:p-8 space-y-6">
        
        {/* Ready Banner */}
        <div className="rounded-xl p-6" style={{ background: 'transparent', border: '1px solid var(--color-verified)' }}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--color-bg-surface)' }}>
              <CheckCircle className="w-6 h-6" style={{ color: 'var(--color-verified)' }} />
            </div>
            <div>
              <h2 className="cs-h2" style={{ color: 'var(--color-verified)' }}>Claim Ready for Submission</h2>
              <p className="cs-body" style={{ color: 'var(--color-verified)' }}>All steps complete. Review the summary below and submit when ready.</p>
            </div>
          </div>
        </div>

        {/* Final Claim Summary */}
        <div id="summary-section" className="rounded-xl p-8 text-center" style={{ background: 'var(--color-brand)' }}>
          <p className="cs-label mb-2" style={{ color: 'var(--color-on-brand)' }}>FINAL CLAIM AMOUNT</p>
          <p className="cs-figure-lg mb-2" style={{ color: 'var(--color-on-brand)' }}><FinancialValue value={finalClaimAmount} cents /></p>
          <p className="cs-body" style={{ color: 'var(--color-on-brand)' }}>Historical Business Interruption Claim</p>
        </div>

        {/* War Room Consensus (downstream deliberation — read-only) */}
        <WarRoomConsensusCard reportId={claimId || reportId} />

        {/* Policy Limit Validation Warning */}
        {policyLimitStatus && (
          <div
            className="rounded-xl p-5"
            style={
              policyLimitStatus.type === 'success'
                ? { background: 'transparent', border: '2px solid var(--color-verified)' }
                : { background: 'var(--color-needs-human-tint)', border: '2px solid var(--color-needs-human-tint-bd)' }
            }
            data-testid="policy-limit-warning"
          >
            <div className="flex items-start gap-4">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={
                  policyLimitStatus.type === 'success'
                    ? { background: 'transparent' }
                    : { background: 'var(--color-needs-human-tint)' }
                }
              >
                {policyLimitStatus.type === 'error' ? (
                  <AlertTriangle className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                ) : policyLimitStatus.type === 'warning' ? (
                  <AlertTriangle className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                ) : (
                  <Shield className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                )}
              </div>
              <div className="flex-1">
                <h4
                  className="cs-body font-bold"
                  style={{ color: policyLimitStatus.type === 'success' ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}
                >
                  {policyLimitStatus.title}
                </h4>
                <p
                  className="cs-meta mt-1"
                  style={{ color: policyLimitStatus.type === 'success' ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}
                >
                  {policyLimitStatus.message}
                </p>

                {/* Progress bar showing usage */}
                <div className="mt-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span
                      className="cs-meta"
                      style={{ color: policyLimitStatus.type === 'success' ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}
                    >
                      {policyLimitStatus.percentage.toFixed(1)}% of limit used
                    </span>
                    <span className="cs-figure" style={{ color: 'var(--color-text-muted)' }}><FinancialValue value={policyLimit} /> limit</span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-bg-elevated)' }}>
                    <div
                      className="h-full transition-all"
                      style={{ width: `${Math.min(policyLimitStatus.percentage, 100)}%`, background: policyLimitStatus.type === 'success' ? 'var(--color-verified)' : 'var(--color-brand)' }}
                    />
                  </div>
                </div>

                {policyLimitStatus.type === 'error' && (
                  <p className="cs-meta mt-2" style={{ color: 'var(--color-needs-human-text)' }}>
                    Consider adjusting calculation parameters or reviewing with your attorney about recovery options.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Key Details */}
        <div id="details-section" className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
              <FileText className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
            </div>
            <h3 className="cs-h3">Key Details</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
              <Building className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-text-dim)' }} />
              <div>
                <p className="cs-label">Business</p>
                <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>{claimantName}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
              <AlertTriangle className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-text-dim)' }} />
              <div>
                <p className="cs-label">Event</p>
                <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>{eventType}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
              <Calendar className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-text-dim)' }} />
              <div>
                <p className="cs-label">Coverage Period</p>
                <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>
                  {eventStartDate && eventEndDate
                    ? `${eventStartDate} - ${eventEndDate}`
                    : 'Per policy period'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
              <Calculator className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-text-dim)' }} />
              <div>
                <p className="cs-label">Calculation Mode</p>
                <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>Standard BI</p>
              </div>
            </div>
          </div>
        </div>

        {/* Calculation Highlights */}
        <div id="calculation-section" className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
              <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
            </div>
            <h3 className="cs-h3">Calculation Highlights</h3>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                <span className="cs-body">Variable Profit Loss</span>
              </div>
              <span className="cs-figure" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={vpLoss} /></span>
            </div>

            <div className="flex justify-between items-center p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                <span className="cs-body">Incremental Lost Profit</span>
              </div>
              <span className="cs-figure" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={incrementalLoss} /></span>
            </div>

            {additionalExpenses > 0 && (
              <div className="flex justify-between items-center p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
                <div className="flex items-center gap-3">
                  <Calculator className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  <span className="cs-body">Additional Expenses</span>
                </div>
                <span className="cs-figure" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={additionalExpenses} /></span>
              </div>
            )}

            {hasMonteCarloData && (
              <div className="flex justify-between items-center p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  <span className="cs-body">Monte Carlo 95% CI</span>
                </div>
                <span className="cs-figure" style={{ color: 'var(--color-text-primary)' }}>
                  <FinancialValue value={monteCarloResults.results.ci_95_low} /> - <FinancialValue value={monteCarloResults.results.ci_95_high} />
                </span>
              </div>
            )}

            <div className="flex justify-between items-center p-4 rounded-lg" style={{ background: 'var(--color-needs-human-tint)', border: '2px solid var(--color-needs-human-tint-bd)' }}>
              <span className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>Total Compensable Loss</span>
              <span className="cs-figure" style={{ fontSize: '1.25rem', color: 'var(--color-needs-human-text)' }}><FinancialValue value={finalClaimAmount} cents /></span>
            </div>
          </div>
        </div>

        {/* Available Downloads */}
        <div id="downloads-section" className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
              <Download className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
            </div>
            <h3 className="cs-h3">Available Downloads</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={handleDownloadPdf}
              disabled={!reportId}
              className="flex items-center gap-3 p-4 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'var(--color-bg-elevated)' }}
            >
              <FileText className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
              <div className="text-left">
                <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>Standard Report</p>
                <p className="cs-meta">PDF • 11 pages</p>
              </div>
            </button>

            <button
              onClick={() => goToStep?.(6)}
              className="flex items-center gap-3 p-4 rounded-lg transition"
              style={{ background: 'var(--color-bg-elevated)' }}
            >
              <Sparkles className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
              <div className="text-left">
                <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>AI Narrative</p>
                <p className="cs-meta">Generate in Step 6</p>
              </div>
            </button>

            <button
              onClick={() => goToStep?.(6)}
              className="flex items-center gap-3 p-4 rounded-lg transition"
              style={{ background: 'var(--color-bg-elevated)' }}
            >
              <Package className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
              <div className="text-left">
                <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>Complete Bundle</p>
                <p className="cs-meta">All reports • 25+ pages</p>
              </div>
            </button>
          </div>
        </div>

        {/* Download Error Message */}
        {downloadError && (
          <div className="rounded-xl p-4 flex items-start justify-between" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-brand)' }} />
              <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>{downloadError}</p>
            </div>
            <button onClick={() => setDownloadError(null)} className="cs-meta ml-2 font-bold" style={{ color: 'var(--color-needs-human-text)' }}>&times;</button>
          </div>
        )}

        {/* Error Message */}
        {submitError && (
          <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
            <AlertTriangle className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-brand)' }} />
            <div>
              <p className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 600 }}>Submission Failed</p>
              <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>{submitError}</p>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="pt-4">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="cs-body w-full font-bold py-4 px-8 rounded-xl shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            style={{ fontSize: '1.125rem', background: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                Submitting Claim...
              </>
            ) : (
              <>
                <CheckCircle className="w-6 h-6" />
                Submit Claim to Dashboard
              </>
            )}
          </button>
          <p className="cs-meta text-center mt-3">
            This will save your completed claim to the dashboard for future reference.
          </p>
        </div>

      </div>
    </WizardV2Layout>
  );
}
